/**
 * Status color utility for packages and pickups
 * This file provides utility functions to get status colors based on status values
 */

// Status color mapping for all package and pickup statuses
export const STATUS_COLOR_MAP = {
  // Package statuses
  order_received: {
    bg: 'bg-blue-500',
    text: 'text-blue-600',
    light: 'bg-blue-50',
    css: 'status-order-received'
  },
  order_confirmed: {
    bg: 'bg-green-500',
    text: 'text-green-600',
    light: 'bg-green-50',
    css: 'status-order-confirmed'
  },
  progressing: {
    bg: 'bg-cyan-500',
    text: 'text-cyan-600',
    light: 'bg-cyan-50',
    css: 'status-progressing'
  },
  to_pick_up: {
    bg: 'bg-purple-500',
    text: 'text-purple-600',
    light: 'bg-purple-50',
    css: 'status-to-pick-up'
  },
  picked_up: {
    bg: 'bg-blue-600',
    text: 'text-blue-700',
    light: 'bg-blue-50',
    css: 'status-picked-up'
  },
  in_transit: {
    bg: 'bg-yellow-500',
    text: 'text-yellow-600',
    light: 'bg-yellow-50',
    css: 'status-in-transit'
  },
  arrived_at_warehouse: {
    bg: 'bg-indigo-500',
    text: 'text-indigo-600',
    light: 'bg-indigo-50',
    css: 'status-arrived-at-warehouse'
  },
  warehouse_processing: {
    bg: 'bg-teal-500',
    text: 'text-teal-600',
    light: 'bg-teal-50',
    css: 'status-warehouse-processing'
  },
  to_deliver: {
    bg: 'bg-lime-500',
    text: 'text-lime-600',
    light: 'bg-lime-50',
    css: 'status-to-deliver'
  },
  self_pickup_putaway: {
    bg: 'bg-emerald-500',
    text: 'text-emerald-600',
    light: 'bg-emerald-50',
    css: 'status-self-pickup-putaway'
  },
  delivering: {
    bg: 'bg-orange-500',
    text: 'text-orange-600',
    light: 'bg-orange-50',
    css: 'status-delivering'
  },
  out_for_delivery: {
    bg: 'bg-orange-500',
    text: 'text-orange-600',
    light: 'bg-orange-50',
    css: 'status-out-for-delivery'
  },
  signed: {
    bg: 'bg-green-600',
    text: 'text-green-700',
    light: 'bg-green-50',
    css: 'status-signed'
  },
  delivered: {
    bg: 'bg-green-600',
    text: 'text-green-700',
    light: 'bg-green-50',
    css: 'status-delivered'
  },
  abnormal_signed: {
    bg: 'bg-amber-500',
    text: 'text-amber-600',
    light: 'bg-amber-50',
    css: 'status-abnormal-signed'
  },
  delivery_attempted: {
    bg: 'bg-orange-600',
    text: 'text-orange-700',
    light: 'bg-orange-50',
    css: 'status-delivery-attempted'
  },
  return_collection: {
    bg: 'bg-red-500',
    text: 'text-red-600',
    light: 'bg-red-50',
    css: 'status-return-collection'
  },
  refused_by_customer: {
    bg: 'bg-red-600',
    text: 'text-red-700',
    light: 'bg-red-50',
    css: 'status-refused-by-customer'
  },
  returned_to_warehouse: {
    bg: 'bg-purple-600',
    text: 'text-purple-700',
    light: 'bg-purple-50',
    css: 'status-returned-to-warehouse'
  },
  cancelled: {
    bg: 'bg-gray-500',
    text: 'text-gray-600',
    light: 'bg-gray-50',
    css: 'status-cancelled'
  },
  placed: {
    bg: 'bg-slate-500',
    text: 'text-slate-600',
    light: 'bg-slate-50',
    css: 'status-placed'
  },
  pending: {
    bg: 'bg-gray-400',
    text: 'text-gray-500',
    light: 'bg-gray-50',
    css: 'status-pending'
  },
  assigned: {
    bg: 'bg-blue-400',
    text: 'text-blue-500',
    light: 'bg-blue-50',
    css: 'status-assigned'
  },
  collected: {
    bg: 'bg-cyan-600',
    text: 'text-cyan-700',
    light: 'bg-cyan-50',
    css: 'status-collected'
  },
  failed: {
    bg: 'bg-red-600',
    text: 'text-red-700',
    light: 'bg-red-50',
    css: 'status-failed'
  },
  rejected: {
    bg: 'bg-red-700',
    text: 'text-red-800',
    light: 'bg-red-50',
    css: 'status-rejected'
  },
  approved: {
    bg: 'bg-green-500',
    text: 'text-green-600',
    light: 'bg-green-50',
    css: 'status-approved'
  }
};

/**
 * Get status colors for a given status
 * @param {string} status - The status value
 * @param {string} type - The type of color ('bg', 'text', 'light', 'css')
 * @returns {string} The CSS class or undefined if not found
 */
export const getStatusColor = (status, type = 'bg') => {
  const normalizedStatus = status?.toLowerCase().replace(/[^a-z_]/g, '_');
  return STATUS_COLOR_MAP[normalizedStatus]?.[type];
};

/**
 * Get background color class for status
 * @param {string} status - The status value
 * @returns {string} The background CSS class
 */
export const getStatusBgColor = (status) => {
  return getStatusColor(status, 'bg') || 'bg-gray-400';
};

/**
 * Get text color class for status
 * @param {string} status - The status value
 * @returns {string} The text CSS class
 */
export const getStatusTextColor = (status) => {
  return getStatusColor(status, 'text') || 'text-gray-600';
};

/**
 * Get light background color class for status
 * @param {string} status - The status value
 * @returns {string} The light background CSS class
 */
export const getStatusLightBgColor = (status) => {
  return getStatusColor(status, 'light') || 'bg-gray-50';
};

/**
 * Get the complete status CSS class name
 * @param {string} status - The status value
 * @returns {string} The complete CSS class name
 */
export const getStatusCssClass = (status) => {
  return getStatusColor(status, 'css') || 'status-pending';
};

/**
 * Check if a status has defined colors
 * @param {string} status - The status value
 * @returns {boolean} True if status has defined colors
 */
export const hasStatusColors = (status) => {
  const normalizedStatus = status?.toLowerCase().replace(/[^a-z_]/g, '_');
  return !!STATUS_COLOR_MAP[normalizedStatus];
};

/**
 * Get all available status colors
 * @returns {Object} Object containing all status color mappings
 */
export const getAllStatusColors = () => {
  return STATUS_COLOR_MAP;
};

/**
 * Format status name for display
 * @param {string} status - The status value
 * @returns {string} Formatted status name
 */
export const formatStatusName = (status) => {
  return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
};

// Export legacy format for backward compatibility
export const STATUS_COLORS = Object.keys(STATUS_COLOR_MAP).reduce((acc, key) => {
  acc[key] = STATUS_COLOR_MAP[key].bg;
  return acc;
}, {});

export default STATUS_COLOR_MAP;
